import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Switch, Select
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, AppstoreOutlined,
    SearchOutlined, CheckCircleFilled, CloseCircleFilled
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import Highlighter from "react-highlight-words";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

const ManageSection = () => {
    const [sections, setSections] = useState([]);
    const [lands, setLands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => { confirm({ closeDropdown: false }); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex); }}>Filter</Button>
                    <Button type="link" size="small" onClick={() => close()}>close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: { onOpenChange(open) { if (open) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]} autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : text,
    });

    const fetchActiveLands = async () => {
        try {
            const res = await api.get('/masterdropdown/listof_activelands.php');
            setLands(res.data.dataset);
        } catch {
            message.error("Failed to load lands.");
        }
    };

    const fetchSections = useCallback(async (page = 1, pageSize = 10) => {
        try {
            setLoading(true);
            const res = await api.get(`/farmSetUp/section/list.php?page=${page}&limit=${pageSize}`);
            setSections(res.data.dataset.map(item => ({
                sectionId: item.section_id,
                landId: item.land_id,
                landName: item.land_name,
                sectionCode: item.section_code,
                sectionName: item.section_name,
                sectionActive: item.section_active,
            })));
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch sections.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSections(1, 10);
        fetchActiveLands();
    }, [fetchSections]);

    const handleTableChange = (pg) => fetchSections(pg.current, pg.pageSize);

    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/farmSetUp/section/add.php', {
                land_id: values.landId,
                section_code: values.sectionCode,
                section_name: values.sectionName,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Section added successfully.");
                setIsAddModalVisible(false);
                fetchSections(pagination.current, pagination.pageSize);
                addForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add section.");
        } finally { setLoading(false); }
    };

    const openEdit = (record) => {
        setCurrentItem(record);
        editForm.setFieldsValue({
            landId: record.landId,
            sectionCode: record.sectionCode,
            sectionName: record.sectionName,
        });
        setIsModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/farmSetUp/section/edit.php', {
                section_id: currentItem.sectionId,
                land_id: values.landId,
                section_code: values.sectionCode,
                section_name: values.sectionName,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Section updated successfully.");
                setIsModalVisible(false);
                fetchSections(pagination.current, pagination.pageSize);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update section.");
        } finally { setLoading(false); }
    };

    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/farmSetUp/section/toggle.php', {
                section_id: record.sectionId,
                section_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchSections(pagination.current, pagination.pageSize);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    const columns = [
        {
            title: 'Land', dataIndex: 'landName', key: 'landName',
            sorter: (a, b) => a.landName.localeCompare(b.landName),
            ...getColumnSearchProps('landName'),
        },
        {
            title: 'Section Code', dataIndex: 'sectionCode', key: 'sectionCode', width: 120,
            ...getColumnSearchProps('sectionCode'),
        },
        {
            title: 'Section Name', dataIndex: 'sectionName', key: 'sectionName',
            sorter: (a, b) => a.sectionName.localeCompare(b.sectionName),
            ...getColumnSearchProps('sectionName'),
        },
        {
            title: 'Status', dataIndex: 'sectionActive', key: 'sectionActive',
            align: 'center', width: 100,
            render: (val) => val == 1
                ? <Tooltip title="Active"><Space size={4}><CheckCircleFilled style={{ color: 'green', fontSize: 16 }} /><Typography.Text style={{ color: 'green' }}>Active</Typography.Text></Space></Tooltip>
                : <Tooltip title="Inactive"><Space size={4}><CloseCircleFilled style={{ color: 'red', fontSize: 16 }} /><Typography.Text style={{ color: 'red' }}>Inactive</Typography.Text></Space></Tooltip>,
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Section">
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    </Tooltip>
                    <Tooltip title={record.sectionActive == 1 ? "Deactivate" : "Activate"}>
                        <Switch
                            size="small"
                            checked={record.sectionActive == 1}
                            onChange={(checked) => handleToggle(record, checked)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                                                    <Breadcrumb
                                className="fluid-breadcrumb"
                                items={[
                                    { href: '/dash', title: <HomeOutlined /> },
                                    { href: '/farmoverview', title: 'Farm Overview' },
                                    { title: 'Manage Sections' },
                                ]}
                            />
                        <h1 className="fluid-page-title">
                            <AppstoreOutlined /> Manage Sections
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Divide AgroLand into manageable blocks or sections</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">Section List</h3>
                        <Space>
                            <Typography.Text type="secondary">{pagination.total} records</Typography.Text>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddModalVisible(true)}>
                                Add New Section
                            </Button>
                        </Space>
                    </div>
                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={sections}
                            rowKey="sectionId"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 800 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            <Modal
                title="Add New Section"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={600}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Land" name="landId" rules={[{ required: true, message: 'Please select land' }]}>
                                <Select allowClear placeholder="Select Land" showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                                    {lands.map(l => <Option key={l.value} value={l.value}>{l.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="Section Code" name="sectionCode" rules={[{ required: true, message: 'Code required' }]}>
                                <Input placeholder="SEC-A" maxLength={10} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={10}>
                            <Form.Item label="Section Name" name="sectionName" rules={[{ required: true, message: 'Name required' }]}>
                                <Input placeholder="Block A - North Side" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Section</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title="Edit Section Details"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); editForm.resetFields(); }}
                footer={null}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Form.Item label="Land" name="landId" rules={[{ required: true }]}>
                        <Select allowClear placeholder="Select Land">
                            {lands.map(l => <Option key={l.value} value={l.value}>{l.label}</Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Section Code" name="sectionCode" rules={[{ required: true }]}>
                        <Input maxLength={10} />
                    </Form.Item>
                    <Form.Item label="Section Name" name="sectionName" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => setIsModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ManageSection;
